<template>
	<div class="h-dvh">
		<Transition name="fade">
			<div
				v-if="chatIsLoading"
				class="absolute inset-0 grid place-items-center bg-white z-50"
			>
				<!-- Loading -->
				<Spinner class="size-8 text-primary"></Spinner>
			</div>
		</Transition>
		<div class="flex flex-col h-full">
			<!-- Header -->
			<div class="z-10 border-b border-gray-light">
				<ChatHeader></ChatHeader>
			</div>

			<!-- Messenger -->
			<Messenger></Messenger>

			<!-- Inputs -->
			<div class="z-10 bg-white border-t border-gray-light py-5">
				<div class="container">
					<MessengerInputs></MessengerInputs>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Messenger from '@/components/Messenger.vue'
import ChatHeader from '@/components/ChatHeader.vue'
import MessengerInputs from '@/components/MessengerInputs.vue'
import { useChatScroll } from '@/composables/useChatScroll'
import {
	ref,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	onUnmounted
} from 'vue'
import { useChatStore } from '@/stores/chatStore'
import Spinner from '@/assets/icons/spinner.svg'
import signalRService from '@/services/signalr'
import { useRouter } from 'vue-router'
import { nextRender } from '@/services/nextRender'
import { useToast } from 'vue-toastification'
const router = useRouter()
const chatStore = useChatStore()
const { isAtBottom } = useChatScroll()
const accessReadyTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const chatIsLoading = ref<boolean>(false)
const ACCESS_READY_TIMEOUT_SECONDS = 20

onBeforeMount(async () => {
	// enter channel
	chatIsLoading.value = true

	try {
		await chatStore.enterChannel()
		await chatStore.initChannel()
	} catch (err) {
		router.push('/')
	}
})

// singals
onMounted(() => {
	setAccessReadyTimeout()

	signalRService.on('AccessReady', () => {
		// Backend is ready to start chatting - enter chat
		chatIsLoading.value = false
		clearAccessReadyTimeout()
	})

	signalRService.on('AccessRevoked', () => {
		// forced kick
		router.push('/')
	})

	signalRService.on('Archived', () => {
		// channel archived
		chatStore.currentChannel!.isArchived = true
		chatStore.currentChannel!.canPatientWrite = false
		chatStore.currentChannel!.isPatientLocked = true
	})

	signalRService.on('ChannelChange', () => {
		// channel changed
		chatStore.fetchChannel()
	})

	signalRService.on(
		'ChangeMessage',
		(accessId: string, messageIdents: string[]) => {
			const idents = messageIdents.reduce<string[]>((stack, element) => {
				const ident = element.split(':')[0]

				if (ident) {
					stack.push(ident)
				}

				return stack
			}, [])

			// channel message changed
			chatStore.updateMessages(idents)
		}
	)

	signalRService.on('NewMessage', async (accessId, messageIdent, authorId?) => {
		// NewMessage arrived
		const lastKnownMessageIndex =
			chatStore.currentChannel?.sessions?.at(-1)?.messages?.at(-1)
				?.messageIndex ?? null

		if (lastKnownMessageIndex) {
			await chatStore.updateMessages(undefined, lastKnownMessageIndex)

			if (isAtBottom || chatStore.currentChannelMember?.id === authorId) {
				// if we are scrolled down or we sent the message, jump down to the bottom
				await nextRender()
				chatStore.handleScroll(false, false)
			}
		}
	})

	signalRService.on('DownloadStatusChanged', async (accessId, state) => {
		if (state.status === 'Failed') {
			chatStore.downloadingAttachmentsFileIdents.delete(state.fileIdent)
			const toast = useToast()
			toast.error(state.errorMessage ?? 'Stahování souboru selhalo.')
			console.error('SignalR event failed', 'DownloadStatusChanged', state)
			return
		}

		if (state.status !== 'Completed' || !state.readSasUri) return
		chatStore.downloadAttachment(
			state.readSasUri,
			state.fileIdent,
			state.fileName,
			state.contentType
		)
	})
})

onBeforeUnmount(() => {
	signalRService.invoke('UnsubscribeAccess') // subscribe is in enterChannel chatStore method
})

onUnmounted(() => {
	clearAccessReadyTimeout()

	// unregister signalR events
	signalRService.off('AccessReady')
	signalRService.off('AccessRevoked')
	signalRService.off('Archived')
	signalRService.off('ChannelChange')
	signalRService.off('ChangeMessage')
	signalRService.off('NewMessage')
	signalRService.off('DownloadStatusChanged')

	// clear store
	chatStore.resetChatView()
})

const setAccessReadyTimeout = () => {
	accessReadyTimeout.value = setTimeout(() => {
		const toast = useToast()
		router.push('/')
		toast.error('Kanál se nepodařilo načíst, opakujte prosím akci.')
		console.error(
			'Channel load timeout',
			'setAccessReadyTimeout',
			ACCESS_READY_TIMEOUT_SECONDS * 1000 + 's'
		)
	}, ACCESS_READY_TIMEOUT_SECONDS * 1000)
}

const clearAccessReadyTimeout = () => {
	if (!accessReadyTimeout.value) {
		return
	}

	clearTimeout(accessReadyTimeout.value)
}
</script>

<style scoped lang="postcss"></style>
